import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface IntegrationCardProps {
  title: string;
  redirectUrl: string;
  image: string;
  summary?: string;
  isConnected?: boolean;
  isConfigurable?: true;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  title,
  summary,
  redirectUrl,
  isConnected,
  image,
  isConfigurable,
}) => {
  const navigate = useNavigate();
  const logEvent = useLogAnalytics();
  return (
    <div
      className={twMerge(
        "border-0.5 border-border flex rounded-xl p-4 w-full max-w-[300px]",
        isConfigurable
          ? "p5 flex-col items-start justify-between"
          : "items-center",
      )}
    >
      <div
        className={twMerge(
          isConfigurable && "mb-6 flex w-full items-start justify-between",
        )}
      >
        <img
          src={image}
          alt={title}
          style={{
            width: isConfigurable ? "70px" : "30px",
            height: isConfigurable ? "70px" : "30px",
          }}
          className="contain rounded-sm"
        />
        {isConnected && (
          <span className="bg-emerald rounded-full px-2 py-1 text-sm font-medium text-white">
            Connected
          </span>
        )}
      </div>
      <h2
        className={twMerge(
          "text-sm font-bold break-words",
          isConfigurable ? "mb-4 text-xl" : "ml-4 mr-auto",
        )}
      >
        {title}
      </h2>
      {summary && (
        <p className="text-muted mb-4 text-sm break-words leading-relaxed">
          {summary}
        </p>
      )}
      {isConfigurable ? (
        <Button
          variant="secondary"
          size="base"
          className="w-full"
          onClick={() => {
            logEvent("integration.select", {
              integration_name: title,
            });
            navigate(redirectUrl);
          }}
          endEnhancer={<ArrowRight />}
        >
          Configure Integration
        </Button>
      ) : (
        <Button
          icon
          variant="secondary"
          size="lg"
          render={<a href={redirectUrl} target="_blank" rel="noreferrer" />}
          onClick={() => {
            logEvent("integration.select", {
              integration_name: title,
            });
          }}
          aria-label="Open integration"
        >
          <ArrowRight />
        </Button>
      )}
    </div>
  );
};

export default IntegrationCard;
